import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  HostBinding,
  HostListener,
  input,
  Input,
  model,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { Commands, Downloads, Results, Search } from '@local/client-contracts';
import { isIconEmoji } from '@local/ts-infra';
import { UiIconModel, type PopupRef } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { getTimeFromNowInText } from '@shared/utils/date-format.util';
import { stopEvent } from '@shared/utils/elements-util';
import { getDownloadIcon, getIconModel } from '@shared/utils/set-icon.util';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SummaryService } from 'src/app/bar/services/summary.service';
import { EventResource } from '../../../results-views/view-item/view-item.component';
import { InvokeCommand } from '../../models/invoke-command.model';
import { SearchResults, type DisplayItemData } from '../../models/results-types';
import type { ActionView } from '../../models/view-filters';
import { actionToIcon } from '../../services/result-command-builder.service';
import {
  getPrimaryActionClick,
  isEnabledSummary,
  isExternalSourceItem,
  isPreviewClickAction,
  isResourceResult,
} from '../../utils/results.util';
import { isExtension } from '@local/common-web';

@UntilDestroy()
@Component({
  selector: 'resource-panel',
  templateUrl: './resource-panel.component.html',
  styleUrls: ['./resource-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourcePanelComponent implements OnInit {
  readonly INDICATE_SUMMARY_ICON: UiIconModel = {
    type: 'font',
    value: 'icon-board-list',
  };
  private onOpenContextMenu: boolean;
  private onOpenSummaryPopup: boolean;
  private _hovered: boolean;
  isExtension = isExtension();
  iconModel: UiIconModel;
  iconOverlayModel: UiIconModel;
  isIconOverlayEmoji: boolean;
  bulletIconModel: UiIconModel;
  createAt: string;
  modifiedAt: string;
  createdBy: string;
  modifiedBy: string;
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  hasSummary: ActionView;
  mainTitleTooltipDisabled = signal(false);
  subTitleTooltipDisabled = signal(false);

  get hovered() {
    return this._hovered;
  }

  model = model<Search.ResultItem>();
  fallbackIcon = input<string>();

  @Input() useAsResultItem: boolean = false;
  @Input() index: number;
  @Input() searchId: string;
  @Input() set hovered(value: boolean) {
    this._hovered = value;
    this.cdr.markForCheck();
  }

  @Output() mouseover = new EventEmitter();
  @Output() mouseout = new EventEmitter();

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  get updatePrefix() {
    return this.isExtension ? 'Updated' : 'Last updated';
  }

  showLinkName = computed(() => isExternalSourceItem(this.model()));

  resourceResultModel = computed(() => (isResourceResult(this.model()) ? (this.model() as Search.ResultResourceItem) : null));

  constructor(
    private resultCommandService: ResultCommandService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
    private summaryService: SummaryService,
    private flagsService: FlagsService,
    protected eventsService: EventsService,
    protected hubService: HubService,
    protected downloadsService: DownloadsService
  ) {}

  ngOnInit() {
    if (this.model()) {
      this.initIcons();
      this.buildData();
      this.initSummary();
    }
  }

  @HostListener('contextmenu', ['$event'])
  async onRightClick(event: MouseEvent) {
    if (this.useAsResultItem && this.model()) {
      this.openContextMenu(event);
      stopEvent(event);
    }
  }

  private initIcons() {
    this.iconModel = getIconModel(this.model()?.view?.icon);
    this.iconOverlayModel = getIconModel(this.model()?.view?.iconOverlay);
    this.isIconOverlayEmoji = isIconEmoji((this.model()?.view?.iconOverlay as Results.Icon)?.lightUrl);
  }

  private initSummary() {
    this.hasSummary = isEnabledSummary((this.model() as DisplayItemData)?.action);
    this.summaryService.popupOpen$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.onOpenSummaryPopup = res;
    });
  }

  private buildData() {
    const traits = this.resourceResultModel()?.resource?.traits;
    if (!traits) return;
    if (traits.modifiedAt) {
      this.modifiedAt = getTimeFromNowInText(traits.modifiedAt).toLowerCase();
    } else if (traits?.createAt) {
      this.createAt = getTimeFromNowInText(traits.createdAt).toLowerCase();
    }
    this.createdBy = traits.createdBy?.name ? `by ${traits.createdBy?.isMe ? 'you' : traits.createdBy?.name}` : '';
    this.modifiedBy = traits.modifiedBy?.name ? `by ${traits.modifiedBy?.isMe ? 'you' : traits.modifiedBy?.name}` : '';
    if (this.createAt || this.modifiedAt) {
      const icon = this.createdBy ? { name: 'icon-user-circle' } : { name: 'icon-history2' };
      this.bulletIconModel = getIconModel(icon);
    }
  }

  openResource() {
    if (getPrimaryActionClick((this.model() as SearchResults)?.action) === 'preview') {
      this.onPreviewClick();
    } else {
      this.openUrl();
    }
  }

  private openUrl() {
    const onClickCommand = this.model()?.view?.title?.onClick;
    if (onClickCommand) {
      this.onInvoke({
        command: onClickCommand,
        trigger: 'mouse_click',
      });
    }
  }

  private onPreviewClick() {
    this.onInvoke({
      command: this.buildPreviewCommand(),
      trigger: 'mouse_click',
    });
  }

  private buildPreviewCommand(): Commands.Preview<SearchResults> {
    return {
      type: 'preview',
      previewable: false,
      state: 'popup',
      model: this.model(),
      selectedIndex: 0,
    };
  }

  private onInvoke(data: InvokeCommand): void {
    this.resultCommandService.onInvoke(
      [this.model()],
      { ...data, invokerItem: this.model(), trigger: 'mouse_click' },
      'result',
      null,
      null,
      this.searchId,
      0,
      0
    );
  }

  async openContextMenu(event: MouseEvent) {
    const openAt: { x: number; y: number } = {
      x: event.x,
      y: event?.y,
    };
    const items = await this.buildContextMenuItems();
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: InvokeCommand) => this.handleContextMenuCommand(item),
      },
      { position: 'below' }
    );
    this.onOpenContextMenu = true;
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.onOpenContextMenu = false;
    });
  }

  private async buildContextMenuItems(): Promise<ContextMenuItem[]> {
    const resourceUrl = (<Commands.OpenUrl>this.model()?.view?.title?.onClick)?.url;

    const items: ContextMenuItem[] = [];
    if (isPreviewClickAction((this.model() as SearchResults)?.action)) {
      items.push({
        id: 'preview',
        text: 'Preview',
        command: { type: 'preview' },
        icon: { type: 'font-icon', value: 'icon-eye' },
      });
    }

    if (resourceUrl) {
      items.push({
        id: 'open',
        text: 'Open',
        icon: { type: 'font-icon', value: 'icon-launch' },
        data: {},
        command: { type: 'open-url', url: resourceUrl } as any,
      });
      items.push({
        id: 'copy-url',
        text: 'Copy URL',
        icon: { type: 'font-icon', value: 'icon-link2' },
        data: {},
        command: { type: 'copy-clipboard', value: resourceUrl } as Commands.DynamicCommand,
      });
    }

    if (this.hasSummary) {
      items.push({
        id: 'summary',
        text: 'Show summary',
        command: {
          command: this.model().view.title.onClick,
          resourceId: this.model().id,
          type: 'summary',
          position: 'center',
        } as Commands.Summary,
        icon: { type: 'font-icon', value: actionToIcon['summary'] },
      });
    }

    const downloadUrl = (<Commands.OpenUrl>this.model()?.view?.title?.onDrag)?.url;
    if (downloadUrl && (await this.isDownloadEnabled())) {
      items.push({
        id: 'download',
        text: 'Download',
        data: { url: downloadUrl },
        command: { type: 'download-url', url: downloadUrl } as Commands.DownloadUrl,
        icon: { type: 'font-icon', value: actionToIcon['download'] },
      });
    }
    return items;
  }

  protected async isDownloadEnabled(): Promise<boolean> {
    return this.flagsService.isEnabled('fileDownloads', this.resourceResultModel().link.id);
  }

  private async handleContextMenuCommand(command: ContextMenuItem) {
    if (command.id === 'preview') {
      this.onInvoke(this.buildInvokeCommand());
    } else if (command.id === 'download') {
      await this.download();
    } else {
      this.onInvoke(command as InvokeCommand);
    }
  }

  private async download() {
    const downloadUrl = (<Commands.OpenUrl>this.model()?.view?.title?.onDrag)?.url;
    const icon = getDownloadIcon(this.model().view.icon, this.model().view.iconOverlay);
    this.eventsService.event('context_menu.download', {
      name: 'action',
      resources: this.getEventResources(),
      location: { title: this.getLocation() },
    });
    const req: Downloads.ResourceDownloadRequest = {
      type: 'Resource',
      url: downloadUrl,
      resource: this.resourceResultModel().resource,
      icon,
    };
    await this.downloadsService.resourceDownload(req);
  }

  protected getLocation() {
    return this.hubService.currentLocation;
  }

  private buildInvokeCommand(): InvokeCommand {
    return {
      command: this.buildPreviewCommand(),
      invokerItem: this.model(),
      trigger: 'context_menu_click',
    };
  }

  private getEventResources(list = 'search_bar_results'): EventResource[] {
    return [this.getEventResource(list)];
  }

  private getEventResource(list = 'search_bar_results'): EventResource {
    return {
      id: this.model().id,
      list,
      type: this.model().type,
      position: 0,
    };
  }

  onMouseout() {
    if (!this.onOpenContextMenu && !this.onOpenSummaryPopup) {
      this.mouseout.emit();
    }
  }

  onMouseover() {
    this.mouseover.emit(this.index);
  }

  onSummary(event?: MouseEvent, resourceId?: string) {
    event.stopPropagation();
    this.onInvoke({
      command: {
        type: 'summary',
        command: this.model()?.view?.title?.onClick,
        position: event,
        resourceId: resourceId || this.model().id,
        resources: this.getEventResources(),
      } as Commands.Summary,
    });
  }

  updateMainTitleTooltip(element: HTMLElement) {
    this.mainTitleTooltipDisabled.set(!(element.offsetWidth < element.scrollWidth));
  }

  updateSubTitleTooltip(element: HTMLElement) {
    this.subTitleTooltipDisabled.set(!(element.offsetWidth < element.scrollWidth));
  }
}
